import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Search, Palette, Code2, Rocket } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';

const steps = [
  {
    num: '01',
    icon: Search,
    title: 'Discovery & Strategy',
    desc: 'Deep-dive into vision, goals, and technical requirements.',
    accent: '#00D4FF',
  },
  {
    num: '02',
    icon: Palette,
    title: 'Design & Architecture',
    desc: 'UX flows, system design, and interactive prototypes.',
    accent: '#7B61FF',
  },
  {
    num: '03',
    icon: Code2,
    title: 'Agile Development',
    desc: 'Two-week sprints with demos, CI/CD, and code reviews.',
    accent: '#C850C0',
  },
  {
    num: '04',
    icon: Rocket,
    title: 'Launch & Scale',
    desc: 'Deployment, monitoring, and ongoing 24/7 support.',
    accent: '#FF6B6B',
  },
];

export default function ProcessSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.8', 'end 0.4'],
  });
  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} className="relative py-24 md:py-32" style={{ background: 'rgba(5,5,16,0.45)', backdropFilter: 'blur(4px)' }}>
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8">
        <SectionLabel>How We Work</SectionLabel>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-[clamp(2rem,4vw,3.5rem)] font-bold font-space text-[#F0F0F5] tracking-[-0.02em] mb-4"
        >
          Our Process
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-[#8A8AA3] text-lg mb-20 md:mb-24 max-w-2xl"
        >
          A proven methodology that delivers results — on time and on budget.
        </motion.p>

        {/* Desktop horizontal timeline */}
        <div className="relative hidden md:block">
          {/* SVG flowing path */}
          <svg
            className="absolute left-0 right-0 top-[58px] w-full h-[2px] overflow-visible"
            viewBox="0 0 1200 2"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="procGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#00D4FF" />
                <stop offset="35%" stopColor="#7B61FF" />
                <stop offset="70%" stopColor="#C850C0" />
                <stop offset="100%" stopColor="#FF6B6B" />
              </linearGradient>
            </defs>
            {/* Track */}
            <line x1="0" y1="1" x2="1200" y2="1" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
            {/* Animated progress */}
            <motion.line
              x1="0"
              y1="1"
              x2="1200"
              y2="1"
              stroke="url(#procGrad)"
              strokeWidth="2"
              style={{ pathLength }}
            />
          </svg>

          <div className="grid grid-cols-4 gap-8 relative">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                viewport={{ once: true }}
                className="relative group"
                data-testid={`process-step-${s.num}`}
              >
                {/* Node — the dot on the timeline */}
                <div className="relative flex justify-center mb-10">
                  <motion.div
                    whileHover={{ scale: 1.15 }}
                    className="relative w-[116px] h-[116px] rounded-full flex items-center justify-center z-10"
                    style={{
                      background: 'radial-gradient(circle, rgba(5,5,16,1), rgba(5,5,16,0.4))',
                      border: `1px solid ${s.accent}30`,
                      boxShadow: `0 0 0 6px rgba(5,5,16,1), 0 0 40px ${s.accent}30`,
                    }}
                  >
                    {/* Pulsing outer ring */}
                    <motion.div
                      animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.3 }}
                      className="absolute inset-0 rounded-full border-2"
                      style={{ borderColor: s.accent }}
                    />
                    {/* Number */}
                    <span
                      className="absolute -top-1 -right-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#050510] border"
                      style={{ color: s.accent, borderColor: `${s.accent}40` }}
                    >
                      {s.num}
                    </span>
                    {/* Icon */}
                    <s.icon className="w-10 h-10" style={{ color: s.accent }} strokeWidth={1.3} />
                  </motion.div>
                </div>

                {/* Content */}
                <div className="text-center px-2">
                  <h3
                    className="text-lg md:text-xl font-bold font-space text-white mb-2 group-hover:text-[var(--hc)] transition-colors"
                    style={{ '--hc': s.accent }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-[#8A8AA3] text-sm leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile vertical stepper */}
        <div className="md:hidden relative pl-10">
          {/* Vertical line */}
          <div
            className="absolute left-[22px] top-0 bottom-0 w-[2px]"
            style={{ background: 'linear-gradient(to bottom, #00D4FF, #7B61FF, #C850C0, #FF6B6B)' }}
          />
          <div className="space-y-10">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div
                  className="absolute -left-[42px] top-0 w-11 h-11 rounded-full flex items-center justify-center bg-[#050510]"
                  style={{ border: `1px solid ${s.accent}40`, boxShadow: `0 0 20px ${s.accent}30` }}
                >
                  <s.icon className="w-4 h-4" style={{ color: s.accent }} />
                </div>
                <div>
                  <div
                    className="text-[10px] font-mono font-bold mb-1 tracking-widest"
                    style={{ color: s.accent }}
                  >
                    STEP {s.num}
                  </div>
                  <h3 className="text-lg font-bold font-space text-white mb-1">{s.title}</h3>
                  <p className="text-[#8A8AA3] text-sm leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
